/**
 * <brand-story-scroll>
 * Pins the carousel stage while the page scrolls vertically and translates the
 * card row horizontally in step with scroll progress (Figma node 1:1667). Once
 * the first card has all but left the viewport the section takes on its pink
 * background for the rest of the scroll (Figma node 118:2696).
 *
 * The pinned behaviour only runs on large viewports where the merchant has left
 * it enabled, the visitor has not requested reduced motion, and the viewport is tall
 * enough to hold the whole composition between the sticky header and the add-to-cart
 * bar. Where the Figma spacing doesn't fit, the group is tightened (`is-compact`, see
 * the CSS); where that still doesn't fit, the card is drawn smaller, down to a floor.
 * Only past that floor is the pin abandoned. Everywhere else the cards fall back to a
 * normal horizontal swipe carousel (handled in CSS).
 *
 * Markup contract:
 *   <brand-story-scroll data-sticky="true">
 *     <div data-track>
 *       <div data-stage>
 *         ...intro...
 *         <... data-progress>
 *         <div data-viewport>
 *           <div data-cards>
 *             <article><div data-media>...</div>...</article> x N
 */
if (!customElements.get('brand-story-scroll')) {
  // How much of the first card has to have passed the row's start (the content edge —
  // on desktop the row runs on past it to the screen edge) before the pink background
  // takes over. Figma calls for "almost scrolled out of view".
  const TINT_AFTER = 0.85;

  // Narrowest the card may be drawn while pinned (the design is 394 wide). Anything
  // below this is what the client called out as far too small, so screens that would
  // need less get the swipe carousel at full size instead.
  const CARD_MIN_WIDTH = 300;

  // Least vertical scroll the pin holds for once the row has anything to travel. A
  // smaller card leaves the row less to cross — at 300px on the Large container only
  // 48px — and a pin that short reads as a stutter rather than a pause.
  const MIN_TRAVEL = 240;

  class BrandStoryScroll extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-track]');
      this.stage = this.querySelector('[data-stage]');
      this.viewport = this.querySelector('[data-viewport]');
      this.cards = this.querySelector('[data-cards]');
      this.progress = this.querySelector('[data-progress]');

      if (!this.track || !this.stage || !this.viewport || !this.cards) return;

      this.overflow = 0;
      this.tintAt = null;
      this.ticking = false;

      this.onScroll = this.onScroll.bind(this);
      this.onSwipe = this.onSwipe.bind(this);
      this.onResize = this.onResize.bind(this);
      this.setup = this.setup.bind(this);

      this.desktop = window.matchMedia('(min-width: 1000px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.desktop.addEventListener('change', this.setup);
      this.reduceMotion.addEventListener('change', this.setup);
      // Whether the stage can hold a whole card depends on the viewport height, and
      // while unpinned nothing else is listening, so this has to sit outside setup().
      window.addEventListener('resize', this.setup, { passive: true });

      this.setup();

      // The fit decision hangs on the intro's height, which moves once the web fonts
      // land. Run it again then so a fallback font can't leave the wrong tier applied.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => {
          if (this.isConnected) this.setup();
        });
      }
    }

    disconnectedCallback() {
      this.teardown();
      this.classList.remove('is-pinned');
      this.desktop.removeEventListener('change', this.setup);
      this.reduceMotion.removeEventListener('change', this.setup);
      window.removeEventListener('resize', this.setup);
    }

    get enabled() {
      return (
        this.dataset.sticky === 'true' &&
        this.desktop.matches &&
        !this.reduceMotion.matches
      );
    }

    setup() {
      this.teardown();

      if (!this.enabled) {
        this.followSwipe();
        return;
      }

      this.classList.add('is-pinned');
      this.measure();

      // The stage holds the intro, the progress bar and a full 394x492 card. Laid out to
      // the Figma spacing the group runs to about 916px, which only clears the sticky
      // header and the add-to-cart bar on viewports of roughly 1060px — taller than most
      // laptops. So where it doesn't fit, tighten the spacing between the three (the CSS
      // behind `is-compact`) and measure again before deciding.
      if (!this.fits()) {
        this.classList.add('is-compact');
        this.measure();
      }

      // Where that still doesn't fit, the card itself gives — in proportion, and never
      // below CARD_MIN_WIDTH. The intro is the merchant's copy and the spacing is already
      // at its floor, so the card is the last thing left to trade.
      if (!this.fits()) {
        this.fitCard();
      }

      // Past the floor the stuck group would sit half off frame or the card would shrink
      // to a sliver (which is what left it at 87px before), so leave those screens
      // unpinned and let the cards swipe at full size.
      if (!this.fits()) {
        this.teardown();
        this.followSwipe();
        return;
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.cards);

      this.update();
    }

    // Unpinned the row is an ordinary horizontal scroller, so the indicator follows its
    // scroll position rather than the page's. It reads the same either way, which is why
    // it stays on screen instead of being reserved for the pinned treatment.
    followSwipe() {
      this.classList.remove('is-pinned');
      this.viewport.addEventListener('scroll', this.onSwipe, { passive: true });

      this.resizeObserver = new ResizeObserver(this.onSwipe);
      this.resizeObserver.observe(this.cards);

      this.onSwipe();
    }

    teardown() {
      window.removeEventListener('scroll', this.onScroll);
      this.viewport.removeEventListener('scroll', this.onSwipe);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      this.track.style.height = '';
      this.cards.style.transform = '';
      this.stage.style.removeProperty('--brand-story-stage-height');
      this.style.removeProperty('--brand-story-card-width');
      this.style.removeProperty('--brand-story-card-scale');
      this.classList.remove('is-tinted', 'is-compact');
      if (this.progress) {
        this.progress.hidden = false;
        this.progress.style.removeProperty('--brand-story-progress');
      }
    }

    // Where the stage comes to rest once stuck, set in CSS so it stays centred in the
    // room below the sticky header.
    stickyTop() {
      return parseFloat(getComputedStyle(this.stage).top) || 0;
    }

    // What the fixed add-to-cart bar takes off the foot of the viewport. The bar publishes
    // its height and the CSS folds it into this property, so reading it back here keeps
    // the fit check and the stage's resting offset working from the same number.
    bottomOffset() {
      return parseFloat(getComputedStyle(this).getPropertyValue('--brand-story-bottom-offset')) || 0;
    }

    // Whether the stuck stage sits wholly in the room between the sticky header and the
    // add-to-cart bar. Its resting offset never comes above the header, so this is also
    // the check that nothing hides beneath either.
    fits() {
      return this.stickyTop() + this.stage.offsetHeight <= window.innerHeight - this.bottomOffset();
    }

    // Draws the card as large as the room allows, in its 394:492 proportion, and never
    // narrower than CARD_MIN_WIDTH. Worked back from the measured excess rather than a
    // formula so it holds for any intro height. A narrower card can wrap a caption onto
    // a second line and ask for a little more, hence the extra passes. Stops at the
    // floor; if that still doesn't fit, setup() falls back to the swipe.
    fitCard() {
      const media = this.cards.querySelector('[data-media]');
      if (!media) return;

      // Design width, read before anything is written over it.
      const full = media.offsetWidth;

      for (let pass = 0; pass < 3 && !this.fits(); pass += 1) {
        const excess = this.stickyTop() + this.stage.offsetHeight - (window.innerHeight - this.bottomOffset());
        // The media is as wide as the card, so trimming its height by the excess means
        // trimming the width by the excess scaled through the aspect ratio.
        const width = Math.max(
          CARD_MIN_WIDTH,
          Math.floor(media.offsetWidth - excess * (media.offsetWidth / media.offsetHeight))
        );

        if (width >= media.offsetWidth) break;

        this.style.setProperty('--brand-story-card-width', `${width}px`);
        // The caption follows the card down (see the CSS), so a long one keeps to a
        // single line instead of wrapping and taking back the room just made.
        this.style.setProperty('--brand-story-card-scale', String(width / full));
        this.measure();
      }
    }

    measure() {
      // The stage's resting offset centres it in the viewport, which CSS can only work
      // out if it knows the height — and that follows the merchant's heading, so it has
      // to be measured. Setting it doesn't change the height, so this settles in one pass.
      this.stage.style.setProperty('--brand-story-stage-height', `${this.stage.offsetHeight}px`);

      this.overflow = Math.max(0, this.cards.scrollWidth - this.viewport.clientWidth);
      this.tintAt = this.tintProgress(this.overflow);
      // Track height = the stage itself plus the vertical scroll the pin holds for: the
      // horizontal distance the cards must travel, or MIN_TRAVEL if that is shorter (a
      // smaller card leaves less to cross, and the cards then move slower than the page
      // rather than 1:1). A sticky element stays stuck for the track's height less its
      // own, so this holds the pin for exactly that long and releases the page the moment
      // it has. Nothing to travel still means nothing to pin for.
      this.travel = this.overflow > 0 ? Math.max(this.overflow, MIN_TRAVEL) : 0;
      this.track.style.height = `${this.stage.offsetHeight + this.travel}px`;
    }

    // Progress through `distance` (how far the row can travel) at which the background
    // turns pink. The same rule serves the pin and the swipe, so the section reads the
    // same on a phone as on a desktop. Null when the cards don't overflow, since the
    // first card never leaves the viewport.
    tintProgress(distance) {
      const first = this.cards.children[0];
      const second = this.cards.children[1];

      if (!first || distance <= 0) return null;

      // The second card's offset is the first card's width plus the gap after it,
      // which is how far the row travels to carry the first card off screen.
      const exit = second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;

      // Capped so the pink always lands inside the travel. With a smaller card the row has
      // less to travel than the first card needs to leave, which would otherwise push
      // this to the very last pixel of the scroll and start the fade as the page moves on.
      return Math.min((exit * TINT_AFTER) / distance, TINT_AFTER);
    }

    onResize() {
      this.measure();
      this.update();
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    onSwipe() {
      if (this.ticking) return;
      this.ticking = true;
      window.requestAnimationFrame(() => {
        const distance = this.viewport.scrollWidth - this.viewport.clientWidth;
        const progress = distance > 0 ? this.viewport.scrollLeft / distance : 0;
        this.setProgress(progress, distance);

        // Swiped rather than scrolled, the row still carries the first card off the
        // same way, so the pink follows it here too (Figma node 118:2696).
        const tintAt = this.tintProgress(distance);
        this.classList.toggle('is-tinted', tintAt !== null && progress >= tintAt);

        this.ticking = false;
      });
    }

    // A bar that can never move reads as broken, so it only shows once there is
    // something past the edge to scroll to.
    setProgress(value, distance) {
      if (!this.progress) return;
      this.progress.hidden = distance <= 0;
      this.progress.style.setProperty('--brand-story-progress', Math.min(Math.max(value, 0), 1));
    }

    update() {
      // The stage is stuck from the moment the track's top passes its resting offset
      // until the track has given up its slack, so progress is how far through that
      // slack the page has scrolled.
      const distance = this.track.offsetHeight - this.stage.offsetHeight;
      const scrolled = this.stickyTop() - this.track.getBoundingClientRect().top;
      const progress = distance > 0 ? Math.min(Math.max(scrolled / distance, 0), 1) : 0;

      this.cards.style.transform = `translate3d(${-(progress * this.overflow)}px, 0, 0)`;
      this.setProgress(progress, this.overflow);

      this.classList.toggle('is-tinted', this.tintAt !== null && progress >= this.tintAt);
    }
  }

  customElements.define('brand-story-scroll', BrandStoryScroll);
}
